import React from 'react';
import { useLocation } from 'react-router-dom';
import { BUSINESS } from '../utils/brand';
import { isLikelyThirdParty } from '../utils/errorOrigin';

/**
 * WHY THIS IS SHAPED THE WAY IT IS
 *
 * On 2026-07-31 two visitors hit `Converting circular structure to JSON` on
 * /services and /client/login. The throw came from outside the bundle: some
 * injected script had replaced appendChild with a wrapper that JSON.stringify's
 * the node handed to it, and React calls appendChild on an <li> when it commits
 * the navbar list. Stringifying an anchor walks __reactFiber$… -> stateNode and
 * hits the cycle.
 *
 * URP does not patch DOM prototypes. The bug was never ours. But the boundary
 * wrapped <Router> itself, so a stranger's browser extension replaced the entire
 * site with "Something went wrong" and the only exit was a manual refresh. One
 * of those two was a client trying to reach their gallery.
 *
 * Three changes:
 *  1. ONE SILENT RETRY. A foreign or transient throw usually clears on a second
 *     render. Nothing is shown during the retry — rendering the fallback for a
 *     frame and snatching it back is worse than a blank tick. Bounded at one, so
 *     a real bug still lands on the fallback immediately after.
 *  2. RESET ON NAVIGATION. `resetKey` clears a tripped boundary when the route
 *     changes. Previously `hasError` was permanent for the life of the document.
 *  3. THE FALLBACK IS A DEAD END NO LONGER. It offers the homepage and the
 *     business phone number, because someone who cannot load /client/login can
 *     still call.
 *
 * Errors are tagged `third_party_script` in Sentry so injected-script noise can
 * be told apart from our own defects without reading every stack by hand.
 */

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, attempt: 0 };
    this.retryTimer = null;
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    const foreign = isLikelyThirdParty(error);
    console.error('React Error Boundary caught:', error, errorInfo);

    try {
      const Sentry = require('@sentry/react');
      Sentry.captureException(error, {
        extra: errorInfo,
        tags: {
          boundary: this.props.name || 'app',
          third_party_script: foreign ? 'yes' : 'no'
        }
      });
    } catch (e) {
      // Sentry not available — continue without monitoring
    }

    // One silent retry, then stop. Guarded on attempt so a deterministic bug
    // cannot loop.
    if (this.state.attempt < 1) {
      this.retryTimer = setTimeout(() => {
        this.setState(s => ({ hasError: false, error: null, attempt: s.attempt + 1 }));
      }, 0);
    }
  }

  componentDidUpdate(prevProps) {
    // Navigating away should clear a tripped boundary. Without this the
    // fallback outlived every route change until the document was reloaded.
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null, attempt: 0 });
    }
  }

  componentWillUnmount() {
    if (this.retryTimer) clearTimeout(this.retryTimer);
  }

  render() {
    if (this.state.hasError) {
      // Retry pending — render nothing rather than flashing the fallback.
      if (this.state.attempt < 1) return null;

      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          padding: '2rem',
          textAlign: 'center'
        }}>
          <h1 style={{ fontSize: '2rem', marginBottom: '1rem', color: 'var(--text-strong)' }}>
            Something went wrong
          </h1>
          <p style={{ color: 'var(--text-muted)', marginBottom: '1.5rem', maxWidth: '520px' }}>
            Sorry about that. Refreshing usually fixes it. If it keeps happening,
            a browser extension or ad blocker is the usual cause &mdash; and you can
            always reach us directly.
          </p>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '12px 28px', backgroundColor: 'var(--text-strong)', color: 'white',
                border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '1rem'
              }}
            >
              Refresh Page
            </button>
            <a
              href="/"
              style={{
                padding: '12px 28px', border: '1px solid var(--text-strong)', color: 'var(--text-strong)',
                borderRadius: '8px', textDecoration: 'none', fontSize: '1rem'
              }}
            >
              Go Home
            </a>
          </div>
          <p style={{ color: 'var(--text-muted)', marginTop: '1.5rem', fontSize: '0.95rem' }}>
            Or call{' '}
            <a href={`tel:+1${BUSINESS.phone.replace(/\D/g, '')}`} style={{ color: 'var(--text-strong)', fontWeight: 600 }}>
              {BUSINESS.phone}
            </a>
          </p>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Route-scoped boundary. Lives INSIDE <Router>, so a page crash leaves the
 * navbar and footer standing and the visitor can navigate out of it. Resets on
 * every path change.
 */
export function RouteErrorBoundary({ children }) {
  const location = useLocation();
  return (
    <ErrorBoundary name="route" resetKey={location.pathname}>
      {children}
    </ErrorBoundary>
  );
}

export default ErrorBoundary;
